// src/pages/admin/Login.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LogIn, User, Lock } from 'lucide-react';
import { authApi } from '../services/api';

interface Props {
  onLoginSuccess?: () => void;
}

const Login: React.FC<Props> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('admin@emprise.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      type LoginResponse = {
        success?: boolean;
        token?: string;
        access_token?: string;
        user?: unknown;
        data?: { token?: string; access_token?: string; user?: unknown };
      };
      let response: LoginResponse = await authApi.login(email, password) as LoginResponse;
      if (!response?.success || !(response?.token || response?.access_token || response?.data?.token || response?.data?.access_token)) {
        const altEmail = email === 'admin@emprisedesign.com' ? 'admin@emprise.com' : email === 'admin@emprise.com' ? 'admin@emprisedesign.com' : email;
        if (altEmail !== email) {
          response = await authApi.login(altEmail, password);
        }
      }
      const token = response?.token || response?.access_token || response?.data?.token || response?.data?.access_token;
      const user = response?.user || response?.data?.user || null;
      if (response?.success && token) {
        localStorage.setItem('token', String(token));
        if (user) localStorage.setItem('user', JSON.stringify(user));
        if (onLoginSuccess) onLoginSuccess();
      } else {
        setError('Invalid email or password');
      }
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen h-screen w-full bg-[#030014] flex items-center justify-center p-4 overflow-hidden relative">
      {/* Background Gradients */}
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-orange-600/20 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-amber-600/20 blur-[120px] rounded-full pointer-events-none"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#0b0c15]/80 backdrop-blur-xl border border-orange-500/20 rounded-2xl p-8 shadow-[0_0_50px_-12px_rgba(249,115,22,0.3)]"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Login</h1>
          <p className="text-gray-400">Sign in to your Emprise Dashboard</p>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-500/20 border border-red-500 text-red-400 rounded-lg text-sm">
            ❌ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User size={18} className="text-gray-500" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="admin@emprisedesign.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-2">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock size={18} className="text-gray-500" />
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-medium rounded-lg shadow-lg shadow-orange-500/25 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-[#0b0c15] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
                <span className="ml-2">Signing in...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <LogIn size={18} className="mr-2" />
                Sign In
              </span>
            )}
          </button>
        </form>

        
      </motion.div>
    </div>
  );
};

export default Login;
